/**
 * Waiting room for a video appointment (ST-20260915-0005, 15 Sep 2026).
 *
 * From 10 minutes before the appointment the client checks their camera and
 * microphone, agrees to the call consent and waits here. Presence is a
 * heartbeat on the appointment, so the therapist sees "Client waiting". When
 * the therapist starts the call it connects straight away: this page joins and
 * opens the call screen, and the ringing box does not appear on this page.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import PreJoinLobby from '../../components/call/PreJoinLobby';
import { useSocket } from '../../context/SocketContext';
import { getPendingCallForMe, joinCall, recordCallConsent } from '../../services/callsApi';
import { holdSessionAwake } from '../../services/sessionManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const HEARTBEAT_MS = 30 * 1000;
const POLL_MS = 5 * 1000;
const OPENS_MINUTES = 10;

const therapistNameOf = (t) =>
  t?.therapistInfo?.displayName || [t?.firstName, t?.lastName].filter(Boolean).join(' ') || t?.name || 'your therapist';
const timeLabel = (d) => new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export default function WaitingRoomPage({ token }) {
  // The waiting room keeps the session awake with no keyboard or mouse input (security batch 2).
  useEffect(() => holdSessionAwake('waiting_room'), []);
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const socket = useSocket();
  const [appointment, setAppointment] = useState(null);
  // loading | too_early | lobby | waiting | connecting | closed | error
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const consentRef = useRef(null);
  const connectingRef = useRef(false);
  const phaseRef = useRef('loading');
  phaseRef.current = phase;

  // The client's own list carries the therapist's display name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/appointments/me`, { headers: { 'x-auth-token': token } });
        const appt = (res.data?.appointments || []).find(a => String(a._id) === String(appointmentId));
        if (cancelled) return;
        if (!appt) {
          setError('We could not find this appointment.');
          setPhase('error');
          return;
        }
        setAppointment(appt);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.msg || 'Could not load your appointment.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentId, token]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15 * 1000);
    return () => clearInterval(id);
  }, []);

  const opensAt = appointment ? new Date(appointment.scheduledStart).getTime() - OPENS_MINUTES * 60 * 1000 : null;
  const closesAt = appointment ? new Date(appointment.scheduledEnd || appointment.scheduledStart).getTime() : null;

  // Before the client presses I'm ready, the phase follows the clock.
  useEffect(() => {
    if (!appointment || ['waiting', 'connecting', 'error'].includes(phaseRef.current)) return;
    if (['cancelled', 'completed', 'no_show'].includes(appointment.status) || now >= closesAt) setPhase('closed');
    else if (now < opensAt) setPhase('too_early');
    else setPhase('lobby');
  }, [appointment, now, opensAt, closesAt]);

  const markWaiting = useCallback(
    () => axios.post(`${API_BASE_URL}/appointments/${appointmentId}/waiting`, {}, { headers: { 'x-auth-token': token } }),
    [appointmentId, token]
  );

  const phaseForRefusal = (err) => {
    const reason = err?.response?.data?.reason;
    if (reason === 'too_early') return 'too_early';
    if (reason) return 'closed';
    return null;
  };

  const handleReady = async ({ consentCopyVersion } = {}) => {
    consentRef.current = consentCopyVersion || null;
    setError('');
    try {
      await markWaiting();
      setPhase('waiting');
    } catch (err) {
      const next = phaseForRefusal(err);
      if (next) setPhase(next);
      else setError(err?.response?.data?.msg || 'Could not join the waiting room. Please try again.');
    }
  };

  const connect = useCallback(async (call) => {
    if (connectingRef.current || !call?.liveCallId) return;
    const therapistId = String(appointment?.therapistId?._id || appointment?.therapistId || '');
    if (therapistId && call.therapistId && String(call.therapistId) !== therapistId) return;
    connectingRef.current = true;
    setPhase('connecting');
    try {
      if (consentRef.current) {
        try {
          await recordCallConsent({ token, liveCallId: call.liveCallId, copyVersion: consentRef.current });
        } catch (consentErr) {
          // The client agreed on screen before waiting; do not block the call on saving it.
          console.warn('[WaitingRoom] consent persist failed:', consentErr.message);
        }
      }
      const { token: livekitToken } = await joinCall({ token, liveCallId: call.liveCallId });
      navigate(`/call/${call.liveCallId}`, { replace: true, state: { livekitToken } });
    } catch (err) {
      connectingRef.current = false;
      setError(err?.response?.data?.msg || 'The call could not connect. Please stay on this page; your therapist can start it again.');
      setPhase('waiting');
    }
  }, [appointment, navigate, token]);

  // While waiting: heartbeat, and connect as soon as the therapist starts the
  // call (socket push, with a poll in case the push is missed).
  useEffect(() => {
    if (phase !== 'waiting') return undefined;
    const heartbeat = setInterval(async () => {
      try {
        await markWaiting();
      } catch (err) {
        const next = phaseForRefusal(err);
        if (next) setPhase(next);
      }
    }, HEARTBEAT_MS);
    const onIncoming = (payload) => connect(payload);
    if (socket) socket.on('server:video:incoming_call', onIncoming);
    const poll = setInterval(async () => {
      try {
        const { call } = await getPendingCallForMe({ token });
        if (call) connect(call);
      } catch (_) { /* the next poll tries again */ }
    }, POLL_MS);
    return () => {
      clearInterval(heartbeat);
      clearInterval(poll);
      if (socket) socket.off('server:video:incoming_call', onIncoming);
    };
  }, [phase, socket, markWaiting, connect, token]);

  // Leaving the page while waiting takes the client out of the waiting room.
  useEffect(() => () => {
    if (phaseRef.current !== 'waiting') return;
    fetch(`${API_BASE_URL}/appointments/${appointmentId}/waiting`, {
      method: 'DELETE',
      keepalive: true,
      headers: { 'x-auth-token': token }
    }).catch(() => {});
  }, [appointmentId, token]);

  const handleLeave = async () => {
    phaseRef.current = 'left';
    try {
      await axios.delete(`${API_BASE_URL}/appointments/${appointmentId}/waiting`, { headers: { 'x-auth-token': token } });
    } catch (_) { /* presence expires within a minute anyway */ }
    navigate('/auth/redirect');
  };

  const backHome = (
    <Button variant="contained" onClick={() => navigate('/auth/redirect')}>Back to your home page</Button>
  );
  const therapistName = therapistNameOf(appointment?.therapistId);

  if (phase === 'loading') {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (phase === 'error' || phase === 'closed') {
    return (
      <Paper sx={{ p: 4, maxWidth: 640, mx: 'auto', my: 6 }}>
        <Typography variant="h5" gutterBottom>Waiting room</Typography>
        <Alert severity={phase === 'error' ? 'error' : 'info'} sx={{ mb: 2 }}>
          {phase === 'error' ? error : 'This appointment has finished or is no longer scheduled.'}
        </Alert>
        {backHome}
      </Paper>
    );
  }

  if (phase === 'too_early') {
    return (
      <Paper sx={{ p: 4, maxWidth: 640, mx: 'auto', my: 6 }}>
        <Typography variant="h5" gutterBottom>Waiting room</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your appointment with {therapistName} starts at {timeLabel(appointment.scheduledStart)}. The waiting room
          opens at {timeLabel(opensAt)}, 10 minutes before. You can leave this page open and it will open by itself.
        </Typography>
        {backHome}
      </Paper>
    );
  }

  if (phase === 'lobby') {
    return (
      <>
        {error && <Alert severity="error" sx={{ maxWidth: 640, mx: 'auto', mt: 4 }}>{error}</Alert>}
        <PreJoinLobby
          title="Waiting room"
          subtitle={`Your appointment with ${therapistName} starts at ${timeLabel(appointment.scheduledStart)}. Check your camera and microphone, then press I'm ready. The call connects when ${therapistName} starts it; it will not ring.`}
          joinLabel="I'm ready"
          onJoin={handleReady}
          requireConsent
        />
      </>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 640, mx: 'auto', my: 6 }}>
      <Stack spacing={2} alignItems="flex-start">
        <Typography variant="h5">
          {phase === 'connecting' ? 'Connecting your call…' : "You're in the waiting room"}
        </Typography>
        {error && <Alert severity="warning">{error}</Alert>}
        <Typography variant="body1" color="text.secondary">
          {phase === 'connecting'
            ? `${therapistName} has started the call.`
            : `${therapistName} can see that you are here. Your appointment starts at ${timeLabel(appointment.scheduledStart)}, and the call will connect by itself when ${therapistName} starts it. Please keep this page open.`}
        </Typography>
        <CircularProgress size={28} />
        {phase === 'waiting' && <Button onClick={handleLeave}>Leave the waiting room</Button>}
      </Stack>
    </Paper>
  );
}
